import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Radiation, Settings, Image as ImageIcon, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Separator } from '../../../components/ui/separator';
import { Spinner } from '../../../components/ui/spinner';
import { PageHeader } from '@/components/ui/page-header';
import { useShow } from '@refinedev/core';

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between py-2">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-right">{value ?? '—'}</span>
  </div>
);

const BoolBadge: React.FC<{ value: boolean }> = ({ value }) => (
  value
    ? <Badge variant="success"><CheckCircle2 className="h-3 w-3 mr-1" />Yes</Badge>
    : <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" />No</Badge>
);

export const ImagingDeviceShow: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { query } = useShow({ resource: 'devices/imaging', id });
  const record = query?.data?.data as any;

  if (query?.isLoading) return <div className="flex justify-center p-12"><Spinner /></div>;
  if (!record) return <div className="p-12 text-center text-muted-foreground">Imaging device not found</div>;

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="-ml-2"><ArrowLeft className="h-4 w-4" /></Button>
            <span>Imaging Device Details</span>
          </div>
        }
        actions={<Button onClick={() => navigate(`/devices/imaging/edit/${record.id}`)}>Edit</Button>}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <Radiation className="h-8 w-8 text-primary" />
                <div>
                  <h2 className="text-xl font-bold">{record.name}</h2>
                  <p className="text-sm text-muted-foreground">{record.manufacturer} {record.model} &bull; S/N: {record.serial_number}</p>
                </div>
              </div>
              <Separator orientation="vertical" className="h-10" />
              <Badge variant="info" className="text-sm px-3 py-1">{record.modality_display || record.modality}</Badge>
              <Separator orientation="vertical" className="h-10" />
              <Badge className="capitalize">{record.status_display || record.status}</Badge>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Studies Received</p>
              <p className="text-3xl font-bold">{(record.study_count ?? record.total_studies_received ?? 0).toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Device & Connection</CardTitle></CardHeader>
          <CardContent className="divide-y">
            <Field label="Category" value={record.category} />
            <Field label="Connector" value={record.connector_name} />
            <Field label="Branch" value={record.branch_name} />
            <Field label="Connection Type" value={record.connection_type_display || record.connection_type} />
            <Field label="Description" value={record.connection_config?.description} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><ImageIcon className="h-5 w-5" /> DICOM Configuration</CardTitle></CardHeader>
          <CardContent className="divide-y">
            <Field label="DICOM Enabled" value={<BoolBadge value={!!record.dicom_enabled} />} />
            <Field label="Calling AE Title" value={record.ae_title ? <code className="text-sm bg-muted px-2 py-0.5 rounded">{record.ae_title}</code> : '—'} />
            <Field label="Device IP" value={record.device_ip ? <code className="text-sm bg-muted px-2 py-0.5 rounded">{record.device_ip}:{record.device_port}</code> : '—'} />
            <Field label="Last Image Received" value={record.last_image_received ? new Date(record.last_image_received).toLocaleString() : 'Never'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> QC & Maintenance</CardTitle></CardHeader>
          <CardContent className="divide-y">
            <Field label="QC Enabled" value={<BoolBadge value={!!record.qc_enabled} />} />
            <Field label="QC Frequency" value={`${record.qc_frequency_hours} hours`} />
            <Field label="Calibration Due" value={record.calibration_due_date ? new Date(record.calibration_due_date).toLocaleDateString() : '—'} />
            <Field label="Maintenance Due" value={record.maintenance_due_date ? new Date(record.maintenance_due_date).toLocaleDateString() : '—'} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImagingDeviceShow;
